import { marketingHref } from "@/components/marketing/marketingLinks";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { StyledAnchor } from "@/components/common/styles";
import {
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import { SectionContainer } from "./HomePrimitives";
import {
  proxyLocationPath,
  type LocationListItem,
} from "@/content/proxyLocations";
import { LOCATIONS } from "./homeContent";
import { BODY_SMALL, HEADING_2 } from "./homeType";

/**
 * Eight whole-card links on a tinted panel. The container carries no vertical
 * padding of its own, the rhythm around this block belongs to its neighbours,
 * which is why it is the one section using the bare `plain` variant.
 */
export default function LocationSection({
  locations,
}: {
  /** The head of the order (`topLocationsCms`). */
  locations: ReadonlyArray<LocationListItem>;
}) {
  return (
    <SectionContainer padding="plain">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          p: "2.5rem",
          borderRadius: "2rem",
          backgroundColor: marketingPalette.surface,
          overflow: "hidden",
          [TINY]: { p: "1.5rem 1rem", borderRadius: "1rem" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "9.6px",
            maxWidth: "50ch",
            mx: "auto",
            textAlign: "center",
          }}
        >
          <Typography component="h2" sx={HEADING_2}>
            {LOCATIONS.heading}
          </Typography>
          <Typography component="p" sx={BODY_SMALL}>
            {LOCATIONS.body}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "8px",
            [NOT_WIDE]: { gridTemplateColumns: "repeat(2, 1fr)" },
          }}
        >
          {locations.map((country) => (
            <StyledAnchor
              key={country.slug}
              href={marketingHref(proxyLocationPath(country.slug))}
              target="_blank"
              rel="noopener"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "12px",
                p: "16px",
                borderRadius: "12px",
                backgroundColor: "background.default",
                border: `1px solid ${marketingPalette.wash}`,
                boxShadow: "0 8px 24px -11px #DCDEE0",
                "&:hover": { borderColor: marketingPalette.inkSubtle },
                [TINY]: { gap: "6.4px", py: "9.6px" },
              }}
            >
              <Box
                sx={{
                  p: "0.4rem",
                  border: `1px solid ${marketingPalette.wash}`,
                  borderRadius: "8px",
                  [TINY]: { p: 0, border: "none" },
                }}
              >
                <Box
                  component="img"
                  src={country.flag}
                  alt=""
                  loading="lazy"
                  sx={{
                    display: "block",
                    width: "2.1rem",
                    height: "2.1rem",
                    [TINY]: { width: "1.8rem", height: "1.8rem" },
                  }}
                />
              </Box>
              <Box
                sx={{
                  fontSize: "1.4rem",
                  fontWeight: 500,
                  overflow: "hidden",
                  [TINY]: { fontSize: "0.9rem", lineHeight: "150%" },
                }}
              >
                {country.name}
              </Box>
              <Box sx={{ display: "flex", gap: "3px" }}>
                <Box
                  sx={{
                    opacity: 0.8,
                    fontSize: "1rem",
                    lineHeight: "140%",
                    [TINY]: { fontSize: "0.85rem" },
                  }}
                >
                  {country.ipCount}
                </Box>
                <Box
                  sx={{
                    opacity: 0.8,
                    fontSize: "1rem",
                    lineHeight: "140%",
                    [TINY]: { fontSize: "0.85rem" },
                  }}
                >
                  {LOCATIONS.unit}
                </Box>
              </Box>
            </StyledAnchor>
          ))}
        </Box>
      </Box>
    </SectionContainer>
  );
}
